import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Environment } from 'components/common/types/Deal.types';
import { NetworkReferenceResponse, ReferenceOptions, UserOptions } from 'store/userOptions/reducer.types';
import { DirectSalesOption } from 'components/common/types/DirectSalesCampaign.types';
import { CodeName } from 'components/common/types/CodeName.types';
import { ProductIdentifier } from 'components/common/types/DirectSalesProductTemplate.types';
import { DataProvidersWithPoiOptions } from 'components/common/types/Planner.types';
import { Taxonomy } from 'components/pages/Planner/PlannerSections/DataTargetingContent/Segments/Segments.types';

const initialState: UserOptions = {
  advertiserOptions: [],
  referenceOptions: {
    location: {
      address: [],
      conurbation: [],
      country: [],
      county: [],
      district: [],
      geographicalAreaLevel1: [],
      geographicalAreaLevel2: [],
      geographicalAreaLevel3: [],
      postcode: [],
      routeTown: [],
      town: [],
      tvArea: [],
    },
    format: {
      businessArea: [],
      frameSizeFamily: [],
      frameSize: [],
      productFormatMarketing: [],
      revenueStream: [],
      productFormat: [],
    },
    tags: {},
  },
  segmentOptions: [],
  locationOptions: [],
  dataProvidersWithPoiOptions: {},
  taxonomy: [],
  networks: [],
  channels: [],
  productList: [],
  venueTaxonomies: [],
};

export const userOptionsSlice = createSlice({
  name: 'userOptions',
  initialState,
  reducers: {
    changeAdvertiserOptions: (state, action: PayloadAction<CodeName[]>) => {
      state.advertiserOptions = action.payload;
    },
    changeSegmentOptions: (state, action) => {
      state.segmentOptions = action.payload;
    },
    addLocationUserOptions: (state, action: PayloadAction<DirectSalesOption[]>) => {
      state.locationOptions = action.payload;
    },
    changeDataProvidersWithPoiOptions: (state, action: PayloadAction<DataProvidersWithPoiOptions>) => {
      state.dataProvidersWithPoiOptions = action.payload;
    },
    changeTaxonomy: (state, action: PayloadAction<Taxonomy[]>) => {
      state.taxonomy = action.payload;
    },
    changeReferenceData: (state, action: PayloadAction<Partial<ReferenceOptions>>) => {
      state.referenceOptions = { ...state.referenceOptions, ...action.payload };
    },
    changeNetworkReferenceData: (state, action: PayloadAction<NetworkReferenceResponse[]>) => {
      state.networks = action.payload;
    },
    changeChannels: (state, action: PayloadAction<Environment[]>) => {
      state.channels = [...action.payload].sort((a, b) => a.name.localeCompare(b.name));
    },
    changeProductList: (state, action: PayloadAction<ProductIdentifier[]>) => {
      state.productList = action.payload;
    },
    changeProductStatus: (state, action: PayloadAction<ProductIdentifier>) => {
      const { id, status } = action.payload;

      const product = state.productList.find((p) => p.id === id);

      if (product) {
        product.status = status;
      }
    },
    setUserOptions: (state, action: PayloadAction<Partial<UserOptions>>) => {
      return { ...state, ...action.payload };
    },
  },
});

export const {
  changeAdvertiserOptions,
  changeSegmentOptions,
  addLocationUserOptions,
  changeDataProvidersWithPoiOptions,
  changeReferenceData,
  changeNetworkReferenceData,
  changeChannels,
  changeProductList,
  changeProductStatus,
  changeTaxonomy,
  setUserOptions,
} = userOptionsSlice.actions;

export default userOptionsSlice.reducer;
